import { forwardRef, useEffect, useRef, useState } from 'react'
import type { Client } from '../../shared/types'

// Type-to-filter combo for selecting a client (day view). Same keyboard model
// as CategoryCombo: typing filters + opens; ArrowUp/Down move the highlight
// when open and bubble to grid navigation when closed; Enter commits.
interface Props {
  value: number | null
  clients: Client[]
  disabled?: boolean
  onCommit: (clientId: number | null) => void
  gridKeyDown: (e: React.KeyboardEvent) => void
  registerRef: (el: HTMLInputElement | null) => void
}

const ClientCombo = forwardRef<HTMLInputElement, Props>(function ClientCombo(
  { value, clients, disabled, onCommit, gridKeyDown, registerRef },
  _ref,
) {
  const selected = clients.find((c) => c.id === value) ?? null
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const display = open ? query : selected?.simple_name ?? ''
  const filtered = open ? clients.filter((c) => c.simple_name.toLowerCase().includes(query.toLowerCase())) : []

  useEffect(() => {
    if (open) setHighlight(0)
  }, [query, open])

  function commit(cl: Client | null) {
    onCommit(cl ? cl.id : null)
    setOpen(false)
    setQuery('')
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (open) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlight((h) => Math.max(h - 1, 0))
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        commit(filtered[highlight] ?? null)
        gridKeyDown(e)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setOpen(false)
        setQuery('')
        return
      }
      if (e.key === 'Tab') {
        commit(filtered[highlight] ?? selected)
        gridKeyDown(e)
        return
      }
      return
    }
    gridKeyDown(e)
  }

  return (
    <div className="relative">
      <input
        ref={(el) => {
          inputRef.current = el
          registerRef(el)
        }}
        className="grid-cell w-full bg-transparent px-2 py-1 font-medium outline-none disabled:text-slate-400"
        value={display}
        disabled={disabled}
        placeholder="select client…"
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onBlur={() => {
          if (open) commit(filtered[highlight] ?? selected)
        }}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-expanded={open}
      />
      {open && filtered.length > 0 && (
        <ul className="absolute left-0 top-full z-20 mt-0.5 max-h-48 w-52 overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg">
          {filtered.map((c, i) => (
            <li key={c.id}>
              <button
                className={'flex w-full items-center gap-2 px-3 py-1 text-left ' + (i === highlight ? 'bg-slate-100' : 'hover:bg-slate-50')}
                onMouseDown={(e) => {
                  e.preventDefault()
                  commit(c)
                }}
              >
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: c.accent_color ?? '#3b6ea5' }} />
                {c.simple_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
})

export default ClientCombo
